import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";

export interface AccessRequest {
  id: string;
  email: string;
  user_id: string | null;
  requested_org_name: string | null;
  matched_org_id: string | null;
  message: string | null;
  status: string;
  reviewed_by: string | null;
  reviewed_at: string | null;
  created_at: string;
  updated_at: string;
}

const tbl = () => supabase.from("access_requests");

export const useMyAccessRequests = () => {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["my_access_requests", user?.id],
    enabled: !!user?.id,
    queryFn: async () => {
      const { data, error } = await tbl().select("*").eq("user_id", user!.id).order("created_at", { ascending: false });
      if (error) throw error;
      return (data || []) as AccessRequest[];
    },
  });
};

export const useSubmitAccessRequest = () => {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async (input: { requested_org_name?: string; matched_org_id?: string; message?: string }) => {
      const { data, error } = await tbl()
        .insert({ email: user!.email!, user_id: user!.id, ...input })
        .select()
        .single();
      if (error) throw error;
      return data as AccessRequest;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["my_access_requests"] });
      toast.success("Access request submitted");
    },
    onError: (e: Error) => toast.error(e.message),
  });
};

export const useAllAccessRequests = () =>
  useQuery({
    queryKey: ["all_access_requests"],
    queryFn: async () => {
      const { data, error } = await tbl().select("*").order("created_at", { ascending: false });
      if (error) throw error;
      return (data || []) as AccessRequest[];
    },
  });

export const useReviewAccessRequest = () => {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async ({ id, status }: { id: string; status: "approved" | "rejected" }) => {
      const { error } = await tbl()
        .update({
          status,
          reviewed_by: user!.id,
          reviewed_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["all_access_requests"] });
      toast.success("Request reviewed");
    },
    onError: (e: Error) => toast.error(e.message),
  });
};
